/**
 * The Live / Drop / Early appt quick filters on Smart Preplan's Review & apply
 * screen (2026-11-03). They narrow the board to drivers carrying a matching move,
 * highlight those moves, and narrow the unassigned list the same way.
 */
import type { PlannedMove, UnassignedMove } from '../../../types/preplanning';

export type LoadFilter = 'live' | 'drop' | 'early';

export const LOAD_FILTERS: ReadonlyArray<{ key: LoadFilter; label: string; noun: string }> = [
  { key: 'live', label: 'Live', noun: 'a live load' },
  { key: 'drop', label: 'Drop', noun: 'a drop load' },
  { key: 'early', label: 'Early appt', noun: 'an appointment at 10:00 or earlier' },
];

type FilterableMove = Pick<PlannedMove | UnassignedMove, 'legnum' | 'legdrop' | 'delivery_leg' | 'is_early_appointment'>;

/**
 * A load is the order's delivery leg, live unless the leg is a drop. Legs before it
 * (prepulls) and after it (returns, terminates) are neither, whatever their tag reads:
 * a pre-assigned move carries no category, so its tag says Live or Drop on any leg.
 */
export const loadKind = (move: FilterableMove): 'live' | 'drop' | null => {
  if (!move.delivery_leg || move.legnum !== move.delivery_leg) return null;
  return move.legdrop === 'Y' ? 'drop' : 'live';
};

/**
 * Live and Drop are alternatives; Early narrows whichever is on, so Live + Early is
 * the early live loads, and Early alone is every move with an early appointment.
 */
export const matchesLoadFilters = (move: FilterableMove, filters: ReadonlySet<LoadFilter>): boolean => {
  const kinds = (['live', 'drop'] as const).filter(kind => filters.has(kind));
  if (kinds.length > 0) {
    const kind = loadKind(move);
    if (!kind || !kinds.includes(kind)) return false;
  }
  return !filters.has('early') || !!move.is_early_appointment;
};
